import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getToken } from '../../utils/tokenStorage';
import { APPLICATION_SECTIONS, labelFor, SMOKE_FREE_STATEMENT } from '../../config/applicationFields';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminApplicationsTab = ({
  applications, filteredApplications, selectedApp, filterStatus, setFilterStatus,
  selectedApps, setSelectedApps, toggleSelectApp, toggleSelectAll, bulkLoading,
  bulkAction, viewApplication, updateApplicationStatus, loading,
  adminNotes, newNote, setNewNote, addNote, downloadCSV
}) => {
  const [membershipClassState, setMembershipClassState] = useState({});
  const [resending, setResending] = useState(false);
  const [memberSummary, setMemberSummary] = useState(null);

  useEffect(() => {
    if (!selectedApp?.application_id) { setMemberSummary(null); return; }
    const token = getToken();
    setMemberSummary(null);
    axios.get(`${API}/admin/applications/${selectedApp.application_id}/member-summary`,
      { withCredentials: true, headers: { Authorization: `Bearer ${token}` } })
      .then(res => setMemberSummary(res.data))
      .catch(() => setMemberSummary(null));
  }, [selectedApp?.application_id]);

  const handleResendApprovals = async () => {
    const scoped = selectedApps.length > 0;
    const confirmMsg = scoped
      ? `Resend the approval email to the ${selectedApps.length} selected applicant(s) who haven't set up their login? Already-registered members will be skipped automatically.`
      : 'Resend approval emails to every approved applicant who has not yet set up their login? Already-registered members will be skipped automatically.';
    if (!window.confirm(confirmMsg)) return;
    setResending(true);
    const token = getToken();
    try {
      const res = await axios.post(
        `${API}/admin/applications/resend-approval-emails`,
        scoped ? { application_ids: selectedApps } : {},
        { withCredentials: true, headers: { Authorization: `Bearer ${token}` } }
      );
      alert(`Done.\nSent: ${res.data.sent}\nAlready registered (skipped): ${res.data.skipped_already_registered}\nFailed: ${res.data.failed}`);
      if (scoped) setSelectedApps([]);
    } catch (e) {
      alert('Resend failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setResending(false);
    }
  };

  const handleClassChange = async (applicationId, newClass) => {
    const token = getToken();
    try {
      const res = await axios.patch(
        `${API}/applications/${applicationId}/membership-class`,
        { membership_class: newClass },
        { withCredentials: true, headers: { Authorization: `Bearer ${token}` } }
      );
      setMembershipClassState(prev => ({ ...prev, [applicationId]: { class: res.data.membership_class, fee: res.data.membership_fee, label: res.data.membership_label } }));
    } catch { alert('Failed to update membership class'); }
  };

  return (
  <div className="dashboard-content">
    <div className="applications-sidebar">
      <div className="filter-bar">
        <h2>Applications</h2>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <button className="btn btn-sm" style={{ fontSize: '12px', padding: '4px 10px' }}
            onClick={() => downloadCSV('/admin/export/applications', 'applications_export.csv')}
            data-testid="export-applications-btn">Export CSV</button>
          <button className="btn btn-sm" style={{ fontSize: '12px', padding: '4px 10px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '6px', cursor: resending ? 'wait' : 'pointer', opacity: resending ? 0.6 : 1 }}
            onClick={handleResendApprovals} disabled={resending}
            data-testid="resend-approvals-btn"
            title={selectedApps.length > 0 ? `Resend approval email to just the ${selectedApps.length} selected applicant(s)` : "Resend approval emails to every approved applicant who hasn't set up their login"}>
            {resending ? 'Sending…' : selectedApps.length > 0 ? `Resend to Selected (${selectedApps.length})` : 'Resend Approval Emails'}
          </button>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="filter-select">
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>
      {selectedApps.length > 0 && (
        <div style={{ padding: '8px 12px', background: '#eff6ff', borderBottom: '1px solid #dbeafe', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e40af' }}>{selectedApps.length} selected</span>
          <button className="btn btn-sm btn-success" onClick={() => bulkAction('approved')} disabled={bulkLoading} data-testid="bulk-approve-btn">{bulkLoading ? '...' : 'Approve All'}</button>
          <button className="btn btn-sm btn-danger" onClick={() => bulkAction('rejected')} disabled={bulkLoading} data-testid="bulk-reject-btn">{bulkLoading ? '...' : 'Reject All'}</button>
          <button className="btn btn-sm" onClick={() => setSelectedApps([])} style={{ fontSize: '12px' }}>Clear</button>
        </div>
      )}
      {loading ? (
        <div className="loading">Loading...</div>
      ) : applications.length === 0 ? (
        <p className="no-applications">No applications found</p>
      ) : (
        <div className="applications-list">
          <div style={{ padding: '6px 12px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input type="checkbox" checked={selectedApps.length === filteredApplications().length && filteredApplications().length > 0} onChange={toggleSelectAll} data-testid="select-all-apps" />
            <span style={{ fontSize: '12px', color: '#6b7280' }}>Select all</span>
          </div>
          {filteredApplications().map((app) => (
            <div key={app.application_id} className={`application-item ${selectedApp?.application_id === app.application_id ? 'active' : ''}`} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
              <input type="checkbox" checked={selectedApps.includes(app.application_id)} onChange={() => toggleSelectApp(app.application_id)} onClick={(e) => e.stopPropagation()} style={{ marginTop: '4px', flexShrink: 0 }} data-testid={`select-app-${app.application_id}`} />
              <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => viewApplication(app)}>
                <div className="app-item-header">
                  <h3>{app.given_name || app.first_name} {app.family_name || app.last_name}</h3>
                  <span className={`status-badge status-${app.status}`}>{app.status}</span>
                </div>
                <p className="app-item-email">{app.email}</p>
                {(app.membership_class || app.membership_type) && (
                  <span style={{ fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '10px', display: 'inline-block', marginTop: '2px',
                    background: app.membership_class === 'gold_envoy' ? '#fef3c7' : app.membership_class === 'waived' || app.membership_class === 'vip' || app.membership_class === 'govt_subsidised' ? '#f3e8ff' : app.membership_type === 'couples' ? '#dbeafe' : '#d1fae5',
                    color: app.membership_class === 'gold_envoy' ? '#92400e' : app.membership_class === 'waived' || app.membership_class === 'vip' || app.membership_class === 'govt_subsidised' ? '#6b21a8' : app.membership_type === 'couples' ? '#1e40af' : '#065f46'
                  }}>
                    {app.membership_class === 'gold_envoy' ? `★ Gold Envoy $20` :
                     app.membership_class === 'govt_subsidised' ? 'Govt Subsidised' :
                     app.membership_class === 'vip' ? 'VIP' :
                     app.membership_class === 'waived' ? 'Fee Waived' :
                     app.membership_type === 'couples' ? 'Couples $290' : 'Single $190'}
                  </span>
                )}
                <p className="app-item-date">{new Date(app.created_at).toLocaleDateString()}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
    <div className="application-details">
      {!selectedApp ? (
        <div className="no-selection"><p>Select an application to view details</p></div>
      ) : (
        <div data-testid="app-details">
          <div className="details-header">
            <h2>{selectedApp.given_name || selectedApp.first_name} {selectedApp.family_name || selectedApp.last_name}</h2>
            <div className="action-buttons">
              {selectedApp.status === 'pending' && (
                <>
                  <button className="btn btn-success" onClick={() => updateApplicationStatus(selectedApp.application_id, 'approved')}>Approve</button>
                  <button className="btn btn-danger" onClick={() => updateApplicationStatus(selectedApp.application_id, 'rejected')}>Reject</button>
                </>
              )}
              <button className="btn btn-sm" style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', fontSize: '12px' }}
                onClick={async () => {
                  if (!window.confirm(`Permanently delete this application from ${selectedApp.email}?`)) return;
                  const token = getToken();
                  try {
                    await axios.delete(`${API}/applications/${selectedApp.application_id}`, { withCredentials: true, headers: { Authorization: `Bearer ${token}` } });
                    alert('Application deleted.');
                    window.location.reload();
                  } catch { alert('Failed to delete application'); }
                }}
                data-testid="delete-application-btn">Delete / Archive</button>
            </div>
          </div>
          <div className="details-sections">
              <div className="details-section">
                <h3>Contact</h3>
                <div className="detail-row"><span className="detail-label">Email:</span><span>{selectedApp.email}</span></div>
                <div className="detail-row"><span className="detail-label">Phone:</span><span>{selectedApp.phone || 'N/A'}</span></div>
                <div className="detail-row">
                  <span className="detail-label">Gender:</span>
                  <span style={{ textTransform: 'capitalize' }}>{(selectedApp.gender || 'N/A').replace('_', ' ')}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Type:</span>
                  <span style={{ fontWeight: '700', textTransform: 'capitalize' }}>{selectedApp.membership_type || 'not set'}</span>
                </div>
                <div className="detail-row" style={{ alignItems: 'flex-start', gap: '8px' }}>
                  <span className="detail-label">Class:</span>
                  <div>
                    <select
                      value={membershipClassState[selectedApp.application_id]?.class || selectedApp.membership_class || 'standard'}
                      onChange={(e) => handleClassChange(selectedApp.application_id, e.target.value)}
                      style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px', fontWeight: '600',
                        background: (membershipClassState[selectedApp.application_id]?.class || selectedApp.membership_class) === 'gold_envoy' ? '#fef3c7' : '#f9fafb' }}
                      data-testid="membership-class-dropdown"
                    >
                      <option value="gold_envoy">★ Gold Envoy — $20 (Introductory)</option>
                      <option value="standard">Standard</option>
                      <option value="govt_subsidised">Govt/NGO Subsidised — $0</option>
                      <option value="vip">VIP/Staff — $0</option>
                      <option value="waived">Fee Waived — $0</option>
                    </select>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '3px' }}>
                      Fee: <strong>${membershipClassState[selectedApp.application_id]?.fee ?? selectedApp.membership_fee ?? '—'}</strong>
                    </div>
                  </div>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Payment:</span>
                  <span data-testid="admin-payment-status">
                    {!memberSummary ? '…' : (
                      <>
                        <span style={{
                          padding: '2px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 700,
                          background: memberSummary.payment_status === 'paid' ? '#d1fae5' : '#fef3c7',
                          color: memberSummary.payment_status === 'paid' ? '#059669' : '#92400e'
                        }}>
                          {memberSummary.payment_status === 'paid' ? 'PAID' : 'PENDING'}
                        </span>
                        {memberSummary.paid_at && (
                          <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '8px' }}>
                            {new Date(memberSummary.paid_at).toLocaleDateString('en-AU')}
                            {memberSummary.paid_amount != null && ` · $${Number(memberSummary.paid_amount).toFixed(2)}`}
                          </span>
                        )}
                        {!memberSummary.registered && <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '8px' }}>not registered yet</span>}
                      </>
                    )}
                  </span>
                </div>
              </div>
            <div className="details-section">
              <h3>Housing Preference</h3>
              <div className="detail-row"><span className="detail-label">Type:</span><span>{selectedApp.shared_housing_type || 'N/A'}</span></div>
              <div className="detail-row"><span className="detail-label">Location:</span><span>{selectedApp.preferred_location || selectedApp.city || 'N/A'}</span></div>
              {(() => {
                const g = selectedApp.gender;
                const t = selectedApp.shared_housing_type;
                const mismatch = (g === 'male' && t === 'single_female') || (g === 'female' && t === 'single_male');
                if (!mismatch) return null;
                return (
                  <div style={{ marginTop: '8px', padding: '8px 12px', background: '#fee2e2', color: '#991b1b', borderRadius: '6px', fontSize: '13px', fontWeight: 600 }} data-testid="gender-mismatch-warning">
                    ⚠ Gender / Accommodation type mismatch — a {g} applicant cannot select a {t.replace('_', ' ')} community.
                  </div>
                );
              })()}
            </div>

            {/* PHOTOS & ID DOCUMENTS */}
            <div className="details-section" data-testid="applicant-documents-section">
              <h3>Photos &amp; ID Documents</h3>
              {!memberSummary ? (
                <p style={{ fontSize: '13px', color: '#6b7280' }}>Loading documents…</p>
              ) : !memberSummary.registered ? (
                <p style={{ fontSize: '13px', color: '#6b7280' }}>Applicant has not set up their login yet — no uploads possible until then.</p>
              ) : memberSummary.documents.length === 0 ? (
                <p style={{ fontSize: '13px', color: '#6b7280' }} data-testid="no-documents-msg">No photos or ID documents uploaded yet.</p>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                  {memberSummary.documents.map(doc => (
                    <a key={doc.doc_id} href={doc.url} target="_blank" rel="noreferrer"
                      style={{ display: 'flex', gap: '8px', alignItems: 'center', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px 10px', textDecoration: 'none', color: '#1a2332' }}
                      data-testid={`admin-doc-${doc.doc_id}`}>
                      {doc.resource_type === 'image'
                        ? <img src={doc.url} alt={doc.label} style={{ width: '54px', height: '54px', objectFit: 'cover', borderRadius: '4px' }} />
                        : <div style={{ width: '54px', height: '54px', background: '#fee2e2', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>PDF</div>}
                      <div>
                        <div style={{ fontSize: '12px', fontWeight: 700 }}>{doc.label}</div>
                        <div style={{ fontSize: '11px', color: '#9ca3af' }}>{new Date(doc.uploaded_at).toLocaleDateString('en-AU')}</div>
                      </div>
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* FULL APPLICATION — every field the member submitted */}
            <div className="details-section" data-testid="full-application-section">
              <h3>Full Application</h3>
              <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 10px' }}>{SMOKE_FREE_STATEMENT}</p>
              {APPLICATION_SECTIONS
                .filter(s => !s.couplesOnly || selectedApp.shared_housing_type === 'couples')
                .map(section => (
                <div key={section.key} style={{ marginBottom: '14px' }}>
                  <h4 style={{ color: '#1e40af', margin: '12px 0 6px', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{section.title}</h4>
                  {section.key === 'personal' && (
                    <div style={{ display: 'flex', padding: '5px 0', borderBottom: '1px solid #f3f4f6', fontSize: '13px' }} data-testid="full-app-email-row">
                      <div style={{ flex: '0 0 220px', color: '#6b7280' }}>Email</div>
                      <div style={{ flex: 1, color: '#1a2332', fontWeight: 500, wordBreak: 'break-word' }}>{selectedApp.email}</div>
                    </div>
                  )}
                  {section.fields.map(field => {
                    const text = labelFor(field, selectedApp[field.key]);
                    return (
                      <div key={field.key} style={{ display: 'flex', padding: '5px 0', borderBottom: '1px solid #f3f4f6', fontSize: '13px' }}>
                        <div style={{ flex: '0 0 220px', color: '#6b7280' }}>{field.label}</div>
                        <div style={{ flex: 1, color: '#1a2332', fontWeight: 500, wordBreak: 'break-word' }}>
                          {text === '' ? <span style={{ color: '#d1d5db' }}>—</span> : text}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
              <div style={{ marginBottom: '14px' }}>
                <h4 style={{ color: '#1e40af', margin: '12px 0 6px', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Submission</h4>
                {[['Status', selectedApp.status],
                  ['Submitted', selectedApp.created_at ? new Date(selectedApp.created_at).toLocaleString('en-AU') : ''],
                  ['Last Updated', selectedApp.updated_at ? new Date(selectedApp.updated_at).toLocaleString('en-AU') : '']].map(([label, value], i) => (
                  <div key={i} style={{ display: 'flex', padding: '5px 0', borderBottom: '1px solid #f3f4f6', fontSize: '13px' }}>
                    <div style={{ flex: '0 0 220px', color: '#6b7280' }}>{label}</div>
                    <div style={{ flex: 1, color: '#1a2332', fontWeight: 500 }}>{value || <span style={{ color: '#d1d5db' }}>—</span>}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="details-section notes-section">
              <h3>Admin Notes</h3>
              <div className="notes-list">
                {adminNotes.length === 0 ? <p className="no-notes">No notes yet</p> : adminNotes.map((note) => (
                  <div key={note.note_id} className="note-item"><p>{note.note}</p><small>{new Date(note.created_at).toLocaleString()}</small></div>
                ))}
              </div>
              <div className="add-note">
                <textarea value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Add a note..." className="input-field" rows="2" />
                <button className="btn btn-primary" onClick={addNote}>Add Note</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  </div>
  );
};

export default AdminApplicationsTab;
